import {
  Component, ChangeDetectionStrategy, ApplicationRef,
  OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject,
} from '@angular/core';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SeriPortService, SeriPortBilgisi, SeriPortDurum } from '@service/fikoyt.seri-port.service';
import { BounceScrollDirective } from '../directives/bounce-scroll.directive';
import { OkuyucuAyarlarComponent } from './okuyucu-ayarlar/okuyucu-ayarlar.component';
import { KartWebSocketService } from '@uygulamalar/hmi-pano/kart-okuyucu/services/kart-websocket.service';
import { hmiAyarGetir } from '../config/hmi-parametre-setleri';

export type NfcDurum = 'bekliyor' | 'tanindi' | 'bilinmiyor' | 'giris-anim' | 'cikis-anim';
export type BaglantiModu = 'seri' | 'websocket';

export interface KartOkumaOlayi {
  uid: string;
  ham: string;
  zaman: Date;
}

@Component({
  selector: 'hmi-kart-okuyucu',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, BounceScrollDirective, OkuyucuAyarlarComponent],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './kart-okuyucu.component.html',
  styleUrl: './kart-okuyucu.component.less',
})
export class HmiKartOkuyucuComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy, OnChanges {
  @Input() nfcDurum: NfcDurum = 'bekliyor';
  /** Görünen durum: animasyon geçişlerini yönetir */
  gorunenDurum: NfcDurum = 'bekliyor';
  @Input() taninanKisi: { isim: string; soyIsim: string; rol: string; resimUrl?: string } | null = null;
  @Input() nfcApiDestekleniyor = false;

  @Output() kartOkundu = new EventEmitter<KartOkumaOlayi>();
  @Output() kartCekildi = new EventEmitter<KartOkumaOlayi>();
  @Output() durumDegisti = new EventEmitter<NfcDurum>();
  @Output() portDurumDegisti = new EventEmitter<SeriPortDurum>();
  @Output() cikisTiklandi = new EventEmitter<void>();
  @Output() testKartKoyuldu = new EventEmitter<void>();
  @Output() testKartCekildi = new EventEmitter<void>();

  // Modal
  ayarlarGorunur = false;
  aktifTab = 0;

  // Seri port state
  portListesi: SeriPortBilgisi[] = [];
  seciliPort: SeriPortBilgisi | null = null;
  seciliBaud = 115200;
  baudSecenekleri = [9600, 19200, 38400, 57600, 115200];
  seriDurum: SeriPortDurum = 'bagli-degil';
  seriHata = '';

  // Socket.IO state
  wsUrl = '';
  wsCihazKimlik = '';
  wsDurum: 'bagli-degil' | 'baglaniyor' | 'bagli' | 'hata' = 'bagli-degil';
  wsHata = '';

  // Monitor log + komut
  seriLog: { zaman: string; veri: string; uidBulundu: boolean; uid: string }[] = [];
  private readonly MAX_LOG = 50;
  komutMetni = '';

  // Test modu
  _testKartAktif = false;
  private readonly _testKartUid = 'TEST-FIKRI-001';

  // Kart varlik takibi
  private _kartVarMi = false;
  private _sonOkunanUid = '';
  private _sonSinyal: 'koyuldu' | 'silindi' | 'koptu' | 'baglandi' | null = null;
  _cihazKopuk = false;
  private _durumBloguAktif = false;

  // Baglanti modu
  baglantiModu: BaglantiModu = 'seri';

  // Toolbar drag scroll
  private _tbDragging = false;
  private _tbStartX = 0;
  private _tbScrollLeft = 0;

  // Kart süre sayacı
  kartSureYazi = '00:00';
  kartSureHaneler: string[] = ['0','0',':','0','0'];
  private _kartSureInterval: any = null;
  private _kartBaslangic: number | null = null;
  private _kartBaslangicCihaz: number | null = null;

  // Çıkış animasyonu
  _cikisIsim = '';
  private _animTimer: any = null;

  // localStorage
  private readonly STORAGE_KEY = 'hmi-kart-okuyucu-ayarlar';

  private _destroy$ = new Subject<void>();

  private appRef = inject(ApplicationRef);

  constructor(
    public seriPort: SeriPortService,
    public wsService: KartWebSocketService,
  ) { super(); }

  // ============ COMPUTED ============

  get aktifBaglanti(): boolean {
    return this.seriDurum === 'bagli' || this.wsDurum === 'bagli';
  }

  get baglantiBilgi(): string {
    if (this.seriDurum === 'bagli') return 'SERI';
    if (this.wsDurum === 'bagli') return 'SOCKET.IO';
    return '';
  }

  // ============ LIFECYCLE ============

  ngOnInit() {
    // Seri port abonelikleri
    this.seriPort.durum$.pipe(takeUntil(this._destroy$)).subscribe(durum => {
      this.seriDurum = durum;
      if (durum === 'bagli') {
        this.baglantiModu = 'seri';
        this.durumSorgula();
      } else if (!this.aktifBaglanti) {
        this._kartVarMi = false;
        this.durumDegisti.emit('bekliyor');
      }
      this.portDurumDegisti.emit(durum);
      this.CD.detectChanges();
    });
    this.seriPort.hataMesaji$.pipe(takeUntil(this._destroy$)).subscribe(msg => {
      this.seriHata = msg;
      this.CD.markForCheck();
    });
    this.seriPort.veriGeldi$.pipe(takeUntil(this._destroy$)).subscribe(veri => {
      this.veriGeldi(veri);
    });

    // WebSocket abonelikleri (zoneless modda detectChanges gerekli)
    this.wsService.durum$.pipe(takeUntil(this._destroy$)).subscribe(durum => {
      this.wsDurum = durum;
      if (durum === 'bagli') {
        this.baglantiModu = 'websocket';
      } else if (!this.aktifBaglanti) {
        this._kartVarMi = false;
        this.durumDegisti.emit('bekliyor');
      }
      this.CD.detectChanges();
    });
    this.wsService.hataMesaji$.pipe(takeUntil(this._destroy$)).subscribe(msg => {
      this.wsHata = msg;
      this.CD.detectChanges();
    });

    // WS kart olaylarini dogrudan dinle (string parse atlaniyor → anlık tepki)
    this.wsService.kartOlayi$.pipe(takeUntil(this._destroy$)).subscribe(olay => {
      const now = new Date();
      const zaman = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;
      if (olay.durum === 'koyuldu' && olay.kartKimlik) {
        this._kartVarMi = true;
        this._sonOkunanUid = olay.kartKimlik;
        this._cihazKopuk = false;
        if (this._koptuTimer) { clearTimeout(this._koptuTimer); this._koptuTimer = null; }
        this._sonSinyal = null;
        // durum-cevap'tan geliyorsa uptime stale — ESP'ye komut gönderildi, cevabı bekle
        // gerçek event ise sıfırdan başla
        console.log('[KART-OLAY]', JSON.stringify({ kaynak: olay.kaynakDurumCevap, uptime: olay.kartUptimeSn, durum: olay.durum }));
        if (!olay.kaynakDurumCevap) {
          this._kartBaslangicCihaz = null;
          this.kartSureBaslat();
        } else if (olay.kartUptimeSn != null && olay.kartUptimeSn >= 0) {
          this._kartBaslangicCihaz = Date.now() - (olay.kartUptimeSn * 1000);
          this.kartSureBaslat();
          console.log('[KART-SAYAC] uptime:', olay.kartUptimeSn, 'sn → baslangic:', Math.floor(olay.kartUptimeSn / 60) + 'dk');
        }
        this.logEkle(zaman, `[WS] Kart Koyuldu: ${olay.kartKimlik}`, true, olay.kartKimlik);
        this.kartOkundu.emit({ uid: olay.kartKimlik, ham: olay.kartKimlik, zaman: now });
      } else if (olay.durum === 'cekildi') {
        this.logEkle(zaman, `[WS] Kart Cekildi: ${olay.kartKimlik || this._sonOkunanUid}`, false, '');
        this.kartSureDurdur();
        this._kartOlayiIsleniyor = true;
        this.kartCekildiIsle(olay.kartKimlik);
        this._kartOlayiIsleniyor = false;
      }
      this.CD.detectChanges();
    });


    // WS veriGeldi$ sadece log/durum bilgisi icin (kart olayları artik kartOlayi$ ile geliyor)
    // ESP'den durum_sorgula cevabı — anlık kart_uptime_sn (seri porttaki DURUM gibi)
    this.wsService.komutCevabi$.pipe(takeUntil(this._destroy$)).subscribe(cevap => {
      if (cevap?.komut_kodu !== 'durum_sorgula' || !cevap?.basarili || !cevap?.mesaj) return;
      try {
        const d = JSON.parse(cevap.mesaj);
        if (d.kart_takili && d.kart_uptime_sn >= 0) {
          this._kartBaslangicCihaz = Date.now() - (d.kart_uptime_sn * 1000);
          this.kartSureBaslat();
        }
      } catch {}
    });

    this.wsService.veriGeldi$.pipe(takeUntil(this._destroy$)).subscribe(veri => {
      // Kart sinyal mesajlarini atla — zaten kartOlayi$ ile isleniyor
      if (/^(Kart Koyuldu|Kart Silindi)$/i.test(veri.trim())) return;
      this.veriGeldi(veri);
      this.CD.detectChanges();
    });

    this.otoBaglanDenemesi();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['nfcDurum']) {
      const yeni = changes['nfcDurum'].currentValue as NfcDurum;

      // bekliyor → tanındı: önce giriş animasyonu göster
      if (yeni === 'tanindi' && this.gorunenDurum !== 'tanindi' && this.gorunenDurum !== 'giris-anim') {
        this.girisAnimasyonuBaslat();
        return;
      }

      // tanındı → bekliyor: çıkış animasyonu göster (kart çekildi vs)
      if (yeni === 'bekliyor' && this.gorunenDurum === 'tanindi') {
        this.cikisAnimasyonuBaslat();
        return;
      }

      // Diğer durumlarda doğrudan geçiş
      this.gorunenDurum = yeni;
      if (yeni === 'tanindi') {
        this.kartSureBaslat();
      } else if (yeni !== 'giris-anim' && yeni !== 'cikis-anim') {
        this.kartSureDurdur();
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
    this.kartSureDurdur();
    if (this._animTimer) { clearTimeout(this._animTimer); }
  }

  /** Zoneless-safe görünüm güncelleme — üç katmanlı garanti */
  private gorunumuGuncelle() {
    this.CD.markForCheck();
    try { this.CD.detectChanges(); } catch (_) {}
    requestAnimationFrame(() => {
      try { this.appRef.tick(); } catch (_) {}
    });
  }

  /** Zamanlayıcı — setTimeout + requestAnimationFrame çift güvenlik */
  private animZamanla(ms: number, fn: () => void) {
    if (this._animTimer) { clearTimeout(this._animTimer); this._animTimer = null; }
    this._animTimer = setTimeout(() => {
      this._animTimer = null;
      fn();
      this.gorunumuGuncelle();
      // Ekstra garanti: bir sonraki frame'de tekrar güncelle
      requestAnimationFrame(() => this.gorunumuGuncelle());
    }, ms);
  }

  /** Giriş: anında tanındı */
  girisAnimasyonuBaslat() {
    this.gorunenDurum = 'tanindi';
    this.kartSureBaslat();
    this.gorunumuGuncelle();
  }

  /** Çıkış: anında bekliyor */
  cikisAnimasyonuBaslat() {
    this._cikisIsim = '';
    this.gorunenDurum = 'bekliyor';
    this.kartSureDurdur();
    this.cikisTiklandi.emit();
    this.gorunumuGuncelle();
  }

  // ============ TOOLBAR DRAG SCROLL ============
  tbDragStart(e: MouseEvent) {
    this._tbDragging = true;
    const el = e.currentTarget as HTMLElement;
    this._tbStartX = e.pageX - el.offsetLeft;
    this._tbScrollLeft = el.scrollLeft;
    el.classList.add('grabbing');
  }
  tbDragMove(e: MouseEvent) {
    if (!this._tbDragging) return;
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = this._tbScrollLeft - (x - this._tbStartX);
  }
  tbDragEnd() { this._tbDragging = false; }
  tbTouchStart(e: TouchEvent) {
    const el = e.currentTarget as HTMLElement;
    this._tbStartX = e.touches[0].pageX - el.offsetLeft;
    this._tbScrollLeft = el.scrollLeft;
  }
  tbTouchMove(e: TouchEvent) {
    const el = e.currentTarget as HTMLElement;
    const x = e.touches[0].pageX - el.offsetLeft;
    el.scrollLeft = this._tbScrollLeft - (x - this._tbStartX);
  }

  // ============ KART SÜRE SAYACI ============

  private kartSureBaslat() {
    // Interval'i temizle ama _kartBaslangic'i koru
    if (this._kartSureInterval) {
      clearInterval(this._kartSureInterval);
      this._kartSureInterval = null;
    }
    // Öncelik: cihaz uptime > mevcut başlangıç > şimdiki an
    if (this._kartBaslangicCihaz) {
      this._kartBaslangic = this._kartBaslangicCihaz;
      this._kartBaslangicCihaz = null;
    } else if (!this._kartBaslangic) {
      this._kartBaslangic = Date.now();
    }
    this._kartSureInterval = setInterval(() => {
      if (!this._kartBaslangic) return;
      const diff = Math.floor((Date.now() - this._kartBaslangic) / 1000);
      const m = Math.floor(diff / 60);
      const s = diff % 60;
      let yazi: string;
      if (m >= 60) {
        const h = Math.floor(m / 60);
        const dm = m % 60;
        yazi = `${h}:${dm.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      } else {
        yazi = `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      }
      this._sureyiGuncelle(yazi);
      this.CD.detectChanges();
    }, 1000);
  }

  private kartSureDurdur() {
    if (this._kartSureInterval) {
      clearInterval(this._kartSureInterval);
      this._kartSureInterval = null;
    }
    this._kartBaslangic = null;
    this._kartBaslangicCihaz = null;
    this._sureyiGuncelle('00:00');
  }

  private _sureyiGuncelle(yazi: string) {
    this.kartSureYazi = yazi;
    this.kartSureHaneler = yazi.split('');
  }

  // ============ OTO BAGLANMA + AYAR KAYDI ============

  private async otoBaglanDenemesi() {
    this.portListesi = await this.seriPort.portlariListele();

    // Parametre sisteminden kart okuyucu ayarlarını oku
    const paramMod = hmiAyarGetir<string>('kartBaglantiModu', '');
    const paramBaud = hmiAyarGetir<number>('baudRate', 0);
    const paramCihazKimlik = hmiAyarGetir<string>('kartOkuyucuCihazKimlik', '');

    const kayitli = this.ayarlariYukle();

    // Parametre sistemi ayarlarını kart okuyucuya uygula (varsa üstüne yaz)
    if (paramMod) {
      this.baglantiModu = paramMod as BaglantiModu;
    }
    if (paramBaud) {
      this.seciliBaud = paramBaud;
    }
    if (paramCihazKimlik) {
      this.wsCihazKimlik = paramCihazKimlik;
    }

    // Oto-bağlan: parametre sisteminden mod varsa onu kullan, yoksa eski kayıtlı ayarları
    const aktifMod = paramMod || kayitli?.mod || '';

    if (aktifMod === 'websocket') {
      this.wsUrl = kayitli?.ws?.url || '';
      if (!this.wsCihazKimlik && kayitli?.ws?.cihazKimlik) {
        this.wsCihazKimlik = kayitli.ws.cihazKimlik;
      }
      const url = this.wsUrl.trim() || undefined;
      const cihaz = this.wsCihazKimlik.trim() || undefined;
      await this.wsService.baglan(url, cihaz);
    } else if (aktifMod === 'seri' || (!aktifMod && kayitli?.seri)) {
      if (!paramBaud && kayitli?.seri?.baudRate) {
        this.seciliBaud = kayitli.seri.baudRate;
      }
      if (this.portListesi.length > 0) {
        const eslesen = kayitli?.seri ? this.portListesi.find(p =>
          p.bilgi.usbVendorId === kayitli.seri!.vid &&
          p.bilgi.usbProductId === kayitli.seri!.pid
        ) : null;
        if (eslesen) {
          this.seciliPort = eslesen;
          await this.seriPort.baglan(eslesen.port, this.seciliBaud);
        } else {
          this.seciliPort = this.portListesi[0];
        }
      }
    } else if (!kayitli && this.portListesi.length > 0) {
      this.seciliPort = this.portListesi[0];
    }

    this.CD.markForCheck();
  }

  private ayarlariKaydet() {
    try {
      const ayar: any = { mod: this.baglantiModu };
      if (this.seciliPort?.bilgi) {
        ayar.seri = {
          vid: this.seciliPort.bilgi.usbVendorId,
          pid: this.seciliPort.bilgi.usbProductId,
          baudRate: this.seciliBaud,
        };
      }
      if (this.wsUrl || this.wsCihazKimlik) {
        ayar.ws = { url: this.wsUrl, cihazKimlik: this.wsCihazKimlik };
      }
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(ayar));
    } catch (_) {}
  }

  private ayarlariYukle(): { mod: BaglantiModu; seri?: { vid: number; pid: number; baudRate: number }; ws?: { url: string; cihazKimlik?: string } } | null {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  }

  // ============ SERI PORT ============

  async yeniPortIste() {
    const yeni = await this.seriPort.portIste();
    if (yeni) {
      const mevcut = this.portListesi.find(p => p.port === yeni.port);
      if (!mevcut) this.portListesi.push(yeni);
      this.seciliPort = yeni;
      // Otomatik baglan ve kaydet
      if (this.wsDurum === 'bagli') this.wsService.kopar();
      const ok = await this.seriPort.baglan(yeni.port, this.seciliBaud);
      if (ok) this.ayarlariKaydet();
      this.CD.markForCheck();
    }
  }

  async seriBaglan() {
    if (!this.seciliPort) return;
    if (this.wsDurum === 'bagli') this.wsService.kopar();
    const ok = await this.seriPort.baglan(this.seciliPort.port, this.seciliBaud);
    if (ok) this.ayarlariKaydet();
  }

  async seriKopar() {
    await this.seriPort.kopar();
  }

  // ============ WEBSOCKET ============

  async wsBaglan() {
    if (this.seriDurum === 'bagli') await this.seriPort.kopar();
    const url = this.wsUrl.trim() || undefined;
    const cihaz = this.wsCihazKimlik.trim() || undefined;
    const ok = await this.wsService.baglan(url, cihaz);
    if (ok) this.ayarlariKaydet();
  }

  wsKopar() {
    this.wsService.kopar();
  }

  // ============ KOMUT GONDER ============

  komutGonder() {
    const cmd = this.komutMetni.trim();
    if (!cmd) return;

    const now = new Date();
    const zaman = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;
    this.logEkle(zaman, '\u25B6 ' + cmd, false, '');

    if (this.seriDurum === 'bagli') {
      this.seriPort.yaz(cmd + '\n');
    } else if (this.wsDurum === 'bagli') {
      this.wsService.yaz(cmd);
    }

    this.komutMetni = '';
    this.CD.markForCheck();
  }

  // ============ KART SINYAL TAKiBi ============

  /** Kart çekildi — hem output emit et hem de kartOlayi$'a push et (shell dinlesin) */
  private _kartOlayiIsleniyor = false;
  private kartCekildiIsle(cekilenUid?: string) {
    const uid = cekilenUid || this._sonOkunanUid;
    this._kartVarMi = false;
    this.durumDegisti.emit('bekliyor');
    if (uid) {
      this.kartCekildi.emit({ uid, ham: 'Kart Cekildi: ' + uid, zaman: new Date() });
      // WS subscribe → kartCekildiIsle → kartOlayi$.next → tekrar subscribe tetiklenmesin
      if (!this._kartOlayiIsleniyor) {
        this.wsService.kartOlayi$.next({
          durum: 'cekildi',
          kartKimlik: uid,
          cihazKimlik: localStorage.getItem('fikoyt_cihaz_kimlik') || '',
          zaman: new Date(),
        });
      }
      this._sonOkunanUid = '';
    }
  }

  /** TEST: Fikri Topaloğlu'nun kartını simüle et — pano'ya yönlendir */
  testKartKoy() {
    this._testKartAktif = true;
    this.testKartKoyuldu.emit();
    this.CD.markForCheck();
  }

  /** TEST: Kartı çekme simülasyonu — pano'ya yönlendir */
  testKartAl() {
    this._testKartAktif = false;
    this.testKartCekildi.emit();
    this.CD.markForCheck();
  }

  private _koptuTimer: any = null;
  private cihazKoptu() {
    // Grace period: 3 saniye içinde tekrar bağlanırsa "koptu" gösterme
    if (this._koptuTimer) return; // zaten bekliyoruz
    this._koptuTimer = setTimeout(() => {
      this._koptuTimer = null;
      // Hâlâ bağlı değilse koptu göster
      if (this.seriDurum !== 'bagli' && !this.wsService.bagli) {
        this._kartVarMi = false;
        this._cihazKopuk = true;
        this.durumDegisti.emit('bekliyor');
        this.CD.detectChanges();
      }
    }, 500);
  }

  private kartSinyalKontrol(veri: string): 'koyuldu' | 'silindi' | 'koptu' | 'baglandi' | null {
    if (/\b(kart.?koyuldu|card.?placed|card.?detected|kart.?algilandi)\b/i.test(veri)) return 'koyuldu';
    if (/\b(kart.?silindi|removed?|no.?card|card.?removed?|cekildi|kart.?yok|card.?gone|absent|silindi)\b/i.test(veri)) return 'silindi';
    if (/(cihaz.?koptu|baglanti.{0,3}kesildi|okuyucu.{0,3}baglanti|disconnected)/i.test(veri)) return 'koptu';
    if (/\[cihaz.?baglandi\]/i.test(veri)) return 'baglandi';
    return null;
  }

  /** Baglanti kurulduktan sonra NodeMCU'ya durum sorgusu gonder (sadece seri port) */
  private durumSorgula() {
    // Seri port modunda: bağlantı stabilize olduktan sonra DURUM komutu gönder
    if (this.seriDurum === 'bagli') {
      setTimeout(() => {
        if (this.seriDurum === 'bagli') {
          this._durumBloguAktif = false;
          this.seriPort.yaz('DURUM\n');
        }
      }, 150);
    }
    // Socket.IO modunda DURUM komutu gonderilemez - olaylar kartOlayi$ ile anlik gelir
  }

  // ============ VERI ISLEME (Seri + WS ortak) ============

  private veriGeldi(veri: string) {
    const now = new Date();
    const zaman = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}.${now.getMilliseconds().toString().padStart(3, '0')}`;

    const temizSatir = veri.trim();

    // ═══ DURUM blok yaniti filtresi ═══
    // Arduino DURUM komutu cok satirli yanit dondurur, sadece "Kart:" satirini isle
    if (temizSatir.includes('═══ CIHAZ DURUMU ═══') || temizSatir.includes('CIHAZ DURUMU')) {
      this._durumBloguAktif = true;
      this.logEkle(zaman, veri, false, '');
      return;
    }
    if (this._durumBloguAktif) {
      // Blok kapanisi
      if (/^═{3,}$/.test(temizSatir)) {
        this._durumBloguAktif = false;
        this.logEkle(zaman, veri, false, '');
        return;
      }
      // "UPTIME:123" satirini yakala (kart ne kadar suredir takili)
      const uptimeMatch = temizSatir.match(/^UPTIME:(\d+)/i);
      if (uptimeMatch) {
        const sn = parseInt(uptimeMatch[1], 10);
        if (sn >= 0) {
          this._kartBaslangicCihaz = Date.now() - (sn * 1000);
          this.kartSureBaslat();
        }
        this.logEkle(zaman, veri, false, '');
        return;
      }
      // "Kart: ABCD1234" satirini yakala
      const kartMatch = temizSatir.match(/^Kart:\s*(.+)/i);
      if (kartMatch) {
        const kartDeger = kartMatch[1].trim();
        this.logEkle(zaman, veri, false, '');
        if (kartDeger && kartDeger.toLowerCase() !== 'yok') {
          const uid = this.normalizeHex(kartDeger);
          if (uid && uid.length >= 4) {
            this._kartVarMi = true;
            this._sonOkunanUid = uid;
            this._cihazKopuk = false;
            this.kartOkundu.emit({ uid, ham: veri, zaman: now });
          }
        }
        // Kart: Yok ise bekliyor durumunda kal
        return;
      }
      // Diger DURUM blok satirlarini sessizce atla (Heap, VCC, IP vs.)
      this.logEkle(zaman, veri, false, '');
      return;
    }

    // UPTIME satiri kontrolu (DURUM blogu disinda da gelebilir)
    const uptimeMatch2 = temizSatir.match(/^UPTIME:(\d+)/i);
    if (uptimeMatch2) {
      const sn = parseInt(uptimeMatch2[1], 10);
      if (sn >= 0) {
        this._kartBaslangicCihaz = Date.now() - (sn * 1000);
        this.kartSureBaslat();
      }
      this.logEkle(zaman, veri, false, '');
      return;
    }

    // Sinyal mesaji kontrolu
    const sinyal = this.kartSinyalKontrol(veri);
    if (sinyal) {
      this._sonSinyal = sinyal;
      this.logEkle(zaman, veri, false, '');
      if (sinyal === 'koyuldu' || sinyal === 'baglandi') {
        this._cihazKopuk = false;
        if (this._koptuTimer) { clearTimeout(this._koptuTimer); this._koptuTimer = null; }
      }
      if (sinyal === 'silindi' && this._kartVarMi) {
        // "Kart Silindi" sonrası gelen UID'yi çekilen kart olarak kullan
        // UID bir sonraki veriGeldi çağrısında gelecek
      }
      if (sinyal === 'koptu') this.cihazKoptu();
      return;
    }

    const uid = this.uidCikar(veri);
    this.logEkle(zaman, veri, !!uid, uid || '');

    if (uid) {
      // "Kart Silindi" sonrasi gelen UID = çekilen kartın UID'si
      if (this._sonSinyal === 'silindi') {
        this._sonSinyal = null;
        this.kartCekildiIsle(uid);
        return;
      }
      this._sonSinyal = null;
      this._kartVarMi = true;
      this._sonOkunanUid = uid;
      this._cihazKopuk = false;
      this.kartOkundu.emit({ uid, ham: veri, zaman: now });
    } else if (this._sonSinyal === 'silindi') {
      // UID gelmedi ama sinyal silindi — eski UID ile çıkış yap
      this._sonSinyal = null;
      this.kartCekildiIsle();
    }
  }

  private logEkle(zaman: string, veri: string, uidBulundu: boolean, uid: string) {
    this.seriLog.push({ zaman, veri, uidBulundu, uid });
    if (this.seriLog.length > this.MAX_LOG) {
      this.seriLog.splice(0, this.seriLog.length - this.MAX_LOG);
    }
    // Monitoru alta kaydir
    setTimeout(() => {
      const el = document.querySelector('.ko-monitor');
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  // ============ UID CIKARTMA ============

  private uidCikar(veri: string): string {
    const temiz = veri.trim();
    if (!temiz) return '';

    // 1) JSON
    if (temiz.startsWith('{')) {
      try {
        const j = JSON.parse(temiz);
        const v = j.uid || j.UID || j.Uid || j.cardId || j.card_id ||
                  j.id || j.ID || j.tag || j.Tag || j.serial || '';
        if (v) return this.normalizeHex(String(v));
      } catch (_) {}
    }

    // 2) Etiketli satirlar
    const etiketRegex = /(?:card\s*)?(?:UID|Tag|PICC|ID|Serial|NUID|Card detected|Kart)\s*[:=]\s*(.+)/i;
    const etiketMatch = temiz.match(etiketRegex);
    if (etiketMatch) {
      const hex = this.normalizeHex(etiketMatch[1]);
      if (hex && hex.length >= 4) return hex;
    }

    // 3) 0x prefix: "0xAB 0xCD 0xEF 0x12"
    const oxMatch = temiz.match(/(?:0x[0-9A-Fa-f]{2}[\s,]*){2,}/);
    if (oxMatch) {
      const hex = oxMatch[0].replace(/0x/gi, '').replace(/[\s,]/g, '').toUpperCase();
      if (hex.length >= 4) return hex;
    }

    // 4) Hex bloklari: "AB CD EF 12", "AB:CD:EF:12"
    const hexBlokRegex = /\b([0-9A-Fa-f]{2}[\s:.-]){1,9}[0-9A-Fa-f]{2}\b/;
    const hexBlokMatch = temiz.match(hexBlokRegex);
    if (hexBlokMatch) {
      const hex = this.normalizeHex(hexBlokMatch[0]);
      if (hex.length >= 4 && hex.length <= 20) return hex;
    }

    // 5) Duz hex string (8+ karakter)
    const duzHexMatch = temiz.match(/\b[0-9A-Fa-f]{4,20}\b/);
    if (duzHexMatch) {
      const hex = duzHexMatch[0].toUpperCase();
      if (hex.length >= 8 || /[0-9]/.test(hex)) return hex;
    }

    return '';
  }

  private normalizeHex(s: string): string {
    return s.replace(/0x/gi, '').replace(/[\s:.\-,]/g, '').toUpperCase();
  }
}
